import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth.js';
import { CloseIcon } from '../components/icons.jsx';

/**
 * Modal de bloqueo: si el usuario no tiene el rol requerido (default 'user'), pide password.
 * Para role='user' se ofrece tambien la opcion de canjear un codigo de invitacion
 * (1 cuento por codigo, sin acceso al admin).
 */
export default function PasswordGate({ children, role = 'user' }) {
  const navigate = useNavigate();
  const { isAuthed, isAdmin, isInvite, login, redeemInvite, logout, busy, error } = useAuth();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState('password');
  const [authMode, setAuthMode] = useState(null);

  useEffect(() => {
    fetch('/api/health').then(res => res.json()).then(data => {
      setAuthMode(data.authMode);
      if (data.authMode === 'invite_code') setMode('invite');
      else setMode('password');

      // Si el usuario tiene una sesión guardada pero el modo de autenticación ha cambiado,
      // cerramos la sesión para obligarle a ver la nueva pantalla.
      const lastAuthMode = localStorage.getItem('stories:last_auth_mode');
      if (lastAuthMode && lastAuthMode !== data.authMode) {
        if (!isAdmin) {
          logout();
        }
      }
      localStorage.setItem('stories:last_auth_mode', data.authMode);

      // (Mantenemos por seguridad la lógica estricta)
      if (isAuthed && !isAdmin) {
        if (data.authMode === 'login' && isInvite) logout();
        else if (data.authMode === 'invite_code' && !isInvite) logout();
      }
    }).catch(console.error);
  }, [isAuthed, isAdmin, isInvite, logout]);

  const meetsRole = role === 'admin' ? isAdmin : isAuthed;
  if (meetsRole) return children;

  // Si esta autenticado como user y le piden admin, le ofrecemos hacer logout primero.
  const needsUpgrade = isAuthed && !isAdmin && role === 'admin';

  if (authMode === 'free_access' && role !== 'admin') {
    // Si es free_access, el backend ya lo permite, no hace falta login para usuarios.
    // Aunque idealmente esto se saltaria el render.
    return children;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <button
          type="button"
          aria-label="Cerrar"
          onClick={() => navigate('/')}
          className="absolute right-3 top-3 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        >
          <CloseIcon size="1.2em" />
        </button>
        <h2 className="pr-8 text-2xl font-bold text-brand-700">
          {role === 'admin' ? 'Acceso administracion' : 'Acceso a Cuentos IA'}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {role === 'admin'
            ? 'Introduce el password de administrador para acceder al panel.'
            : mode === 'password'
              ? 'Introduce el password para empezar a generar cuentos. Solo te lo preguntaremos una vez en este navegador.'
              : 'Introduce el codigo de invitacion que te ha facilitado el administrador. El codigo te permite crear un unico cuento.'}
        </p>

        {role === 'user' && authMode !== 'login' && authMode !== 'invite_code' && (
          <div className="mt-4 flex gap-1 rounded-lg bg-gray-100 p-1 text-sm">
            <button
              type="button"
              className={`flex-1 rounded-md px-3 py-1.5 font-medium transition ${
                mode === 'password' ? 'bg-white text-brand-700 shadow' : 'text-gray-600'
              }`}
              onClick={() => setMode('password')}
            >
              Password
            </button>
            <button
              type="button"
              className={`flex-1 rounded-md px-3 py-1.5 font-medium transition ${
                mode === 'invite' ? 'bg-white text-brand-700 shadow' : 'text-gray-600'
              }`}
              onClick={() => setMode('invite')}
            >
              Codigo de invitacion
            </button>
          </div>
        )}

        {(role === 'admin' || mode === 'password') && (
          <form
            className="mt-4 space-y-3"
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await login(password, role);
              if (ok) setPassword('');
            }}
          >
            <input
              autoFocus
              type="password"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy || !password}
              className="w-full rounded-lg bg-brand-600 px-4 py-2 font-semibold text-white shadow hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'Comprobando...' : 'Entrar'}
            </button>
            {needsUpgrade && (
              <p className="text-center text-xs text-gray-500">
                Estas con sesion de usuario.{' '}
                <button type="button" className="underline" onClick={logout}>
                  Cerrar sesion
                </button>
              </p>
            )}
          </form>
        )}

        {role === 'user' && mode === 'invite' && (
          <form
            className="mt-4 space-y-3"
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await redeemInvite(code.trim().toUpperCase());
              if (ok) setCode('');
            }}
          >
            <input
              autoFocus
              type="text"
              autoCapitalize="characters"
              spellCheck={false}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono uppercase tracking-wider outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              placeholder=""
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy || code.trim().length < 4}
              className="w-full rounded-lg bg-brand-600 px-4 py-2 font-semibold text-white shadow hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'Validando...' : 'Canjear codigo'}
            </button>
            <p className="text-center text-xs text-gray-500">
              El codigo te permitira crear <strong>un solo</strong> cuento.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
